import {useEffect} from 'react';
import {useQueryClient} from 'react-query';

import {atom, useAtom} from 'jotai';
import {CartAdditionalGroupProps, CartAdditionalProps} from '../types';
import {
  AdditionalGroupConfigProps,
  AdditionalGroupConfigTypeEnum,
  ProductProps,
} from 'modules/products/types';
import productsKeys from 'modules/products/util/products-keys';
import useQueryHelpers from 'shared/hooks/use-query-helpers';

const additionalGroupsCartAtom = atom<CartAdditionalGroupProps>({});

const useAdditionalGroupsCart = (
  productId: string,
  defaultValue?: CartAdditionalGroupProps,
) => {
  const [additionalGroupsCart, setAdditionalGroupsCart] = useAtom(
    additionalGroupsCartAtom,
    {},
  );

  useEffect(() => {
    if (defaultValue) setAdditionalGroupsCart(defaultValue);
  }, []);

  const {getData} = useQueryHelpers();
  const product = getData<ProductProps>(productsKeys.details(productId));

  const getAdditionalGroupConfig = (additionalGroupConfigId: string) =>
    product?.additionalGroupsConfig.find(
      (additionalGroupConfig) =>
        additionalGroupConfig.id === additionalGroupConfigId,
    );

  const getAdditionalGroupCart = (additionalGroupConfigId: string) =>
    additionalGroupsCart[additionalGroupConfigId];

  const getAdditional = (
    additionalGroupConfigId: string,
    additionalId: string,
  ) =>
    getAdditionalGroupConfig(
      additionalGroupConfigId,
    )?.additionalGroup.additionals.find(
      (additional) => additional.id === additionalId,
    );

  const toggleAdditional = (
    additionalGroupConfigId: string,
    additionalId: string,
  ) =>
    setAdditionalGroupsCart((prev) => ({
      ...prev,
      [additionalGroupConfigId]: [{id: additionalId, quantity: 1}],
    }));

  const addAdditional = (
    additionalGroupConfigId: string,
    additionalId: string,
  ) => {
    const additionalsDraft = getAdditionalGroupCart(additionalGroupConfigId);

    if (!additionalsDraft)
      return toggleAdditional(additionalGroupConfigId, additionalId);

    const isAdditional = additionalsDraft.find(
      (additional) => additional.id === additionalId,
    );

    if (isAdditional)
      return setAdditionalGroupsCart((prev) => ({
        ...prev,
        [additionalGroupConfigId]: additionalsDraft.map((additional) =>
          additional.id === additionalId
            ? {...additional, quantity: additional.quantity + 1}
            : additional,
        ),
      }));

    setAdditionalGroupsCart((prev) => ({
      ...prev,
      [additionalGroupConfigId]: [
        ...additionalsDraft,
        {id: additionalId, quantity: 1},
      ],
    }));
  };

  const removeAdditional = (
    additionalGroupConfigId: string,
    additionalId: string,
  ) => {
    const additionalsDraft = getAdditionalGroupCart(additionalGroupConfigId);

    if (!additionalsDraft) return;

    const quantity = additionalsDraft.reduce(
      (total, additional) => total + additional.quantity,
      0,
    );

    if (quantity === 1)
      return setAdditionalGroupsCart((prev) => {
        const {[additionalGroupConfigId]: deleted, ...rest} = prev;
        return rest;
      });

    setAdditionalGroupsCart((prev) => ({
      ...prev,
      [additionalGroupConfigId]: additionalsDraft
        .map((additional) =>
          additional.id === additionalId
            ? {...additional, quantity: additional.quantity - 1}
            : additional,
        )
        .filter((additional) => additional.quantity > 0),
    }));
  };

  const sumAdditionalsPrice = (
    additionalGroupCartId: string,
    cartAdditionals: CartAdditionalProps[],
    {isFree, additionalPricesWhenFree}: AdditionalGroupConfigProps,
  ) =>
    cartAdditionals.reduce((total, {id, quantity}) => {
      const additional = getAdditional(additionalGroupCartId, id);

      if (!additional) return total;

      const chargeWhenFreePrice =
        additionalPricesWhenFree?.find((additional) => additional.id === id)
          ?.price ?? 0;

      const price = !isFree ? additional.price : chargeWhenFreePrice;

      return total + price * quantity;
    }, 0);

  const avarageAdditionalsPrice = (
    additionalGroupCartId: string,
    cartAdditionals: CartAdditionalProps[],
    additionalGroupConfig: AdditionalGroupConfigProps,
  ) => {
    const additionalsPrice = sumAdditionalsPrice(
      additionalGroupCartId,
      cartAdditionals,
      additionalGroupConfig,
    );

    const quantity = cartAdditionals.reduce(
      (total, cartAdditional) => total + cartAdditional.quantity,
      0,
    );

    return additionalsPrice / quantity;
  };

  const highestAdditionalsPrice = (
    additionalGroupCartId: string,
    cartAdditionals: CartAdditionalProps[],
    {isFree, additionalPricesWhenFree}: AdditionalGroupConfigProps,
  ) =>
    cartAdditionals.reduce((highestValue, {id}) => {
      const additional = getAdditional(additionalGroupCartId, id);

      if (!additional) return highestValue;

      const chargeWhenFreePrice =
        additionalPricesWhenFree?.find((additional) => additional.id === id)
          ?.price ?? 0;

      const price = !isFree ? additional.price : chargeWhenFreePrice;

      return price > highestValue ? price : highestValue;
    }, 0);

  const additionalsPrice = Object.keys(additionalGroupsCart).reduce(
    (total, additionalGroupCartId) => {
      const additionalGroupConfig = getAdditionalGroupConfig(
        additionalGroupCartId,
      );

      if (!additionalGroupConfig) return total;

      const additionals = getAdditionalGroupCart(additionalGroupCartId);

      let additionalsPrice = sumAdditionalsPrice(
        additionalGroupCartId,
        additionals,
        additionalGroupConfig,
      );

      if (additionalGroupConfig.type === AdditionalGroupConfigTypeEnum.average)
        additionalsPrice = avarageAdditionalsPrice(
          additionalGroupCartId,
          additionals,
          additionalGroupConfig,
        );

      if (
        additionalGroupConfig.type ===
        AdditionalGroupConfigTypeEnum.highest_value
      )
        additionalsPrice = highestAdditionalsPrice(
          additionalGroupCartId,
          additionals,
          additionalGroupConfig,
        );

      return additionalsPrice + total;
    },
    0,
  );

  return {
    additionalGroupsCart,
    setAdditionalGroupsCart,
    addAdditional,
    removeAdditional,
    toggleAdditional,
    getAdditionalGroupCart,
    additionalsPrice,
  };
};

export default useAdditionalGroupsCart;
